import { ChangeEventHandler, useCallback, useMemo, Fragment } from 'react';

import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useOtherDiseaseLabels } from '@/hooks/grade-flow/use-other-diseases-labels';
import { useGradeOtherDiseaseLabel } from '@/hooks/use-grade-other-disease-label';
import { useGradeFlow } from '@/states/grade-flow';
import { OtherDiseaseKey } from '@/states/grade-flow/types';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { MultiDecisionButtons } from '@/components/form/multi-decision-buttons';
import { Fieldset } from '@/components/form/fieldset';
import { Label } from '@/components/form/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/form/textarea';

export function OtherDiseaseQuestion() {
  const { t } = useTranslation();
  const { getDiseaseLabel } = useGradeOtherDiseaseLabel();

  const { DISEASE_FOLLOW_UP_OPTIONS, DISEASE_OPTIONS_DROPDOWN } = useOtherDiseaseLabels();

  const {
    form,
    isDisabled,
    isOtherCustomDiseaseSelected,
    isOtherDiseaseNoneSelected,
    setIsOtherDiseaseNoneSelected,
    setIsOtherCustomDiseaseSelected,
    setOtherDiseaseValue,
    setCustomDiseaseValue,
    setOtherDiseaseFollowUp,
  } = useGradeFlow(
    useShallow((s) => ({
      isDisabled: s.form.is_readable === undefined || s.isSavingGrade,
      isOtherCustomDiseaseSelected: s.isOtherCustomDiseaseSelected,
      isOtherDiseaseNoneSelected: s.isOtherDiseaseNoneSelected,
      setIsOtherDiseaseNoneSelected: s.setIsOtherDiseaseNoneSelected,
      setIsOtherCustomDiseaseSelected: s.setIsOtherCustomDiseaseSelected,
      setOtherDiseaseValue: s.setOtherDiseaseValue,
      setCustomDiseaseValue: s.setCustomDiseaseValue,
      setOtherDiseaseFollowUp: s.setOtherDiseaseFollowUp,
      form: s.form,
    }))
  );

  const otherSelectLabel = useMemo(() => {
    return getDiseaseLabel(
      [
        isOtherCustomDiseaseSelected,
        Boolean(form.other_disease?.concern_for_glaucoma),
        Boolean(form.other_disease?.macular_drusen),
        Boolean(form.other_disease?.macular_pigmentary_disturbance),
        Boolean(form.other_disease?.epiretinal_membrane),
        Boolean(form.other_disease?.choroidal_lesion),
      ],
      isOtherDiseaseNoneSelected
    );
  }, [
    getDiseaseLabel,
    form.other_disease?.choroidal_lesion,
    form.other_disease?.concern_for_glaucoma,
    form.other_disease?.epiretinal_membrane,
    form.other_disease?.macular_drusen,
    form.other_disease?.macular_pigmentary_disturbance,
    isOtherCustomDiseaseSelected,
    isOtherDiseaseNoneSelected,
  ]);

  const enableFollowUpQuestion = useMemo(() => {
    return (
      isOtherCustomDiseaseSelected ||
      Boolean(form.other_disease?.concern_for_glaucoma) ||
      Boolean(form.other_disease?.macular_drusen) ||
      Boolean(form.other_disease?.macular_pigmentary_disturbance) ||
      Boolean(form.other_disease?.epiretinal_membrane) ||
      Boolean(form.other_disease?.choroidal_lesion)
    );
  }, [
    isOtherCustomDiseaseSelected,
    form.other_disease?.choroidal_lesion,
    form.other_disease?.concern_for_glaucoma,
    form.other_disease?.epiretinal_membrane,
    form.other_disease?.macular_drusen,
    form.other_disease?.macular_pigmentary_disturbance,
  ]);

  const handleSelectOtherDisease = useCallback(
    (selected?: boolean) => setIsOtherDiseaseNoneSelected(Boolean(selected)),
    [setIsOtherDiseaseNoneSelected]
  );

  const handleSelectCustomDisease = useCallback(
    (selected?: boolean) => setIsOtherCustomDiseaseSelected(Boolean(selected)),
    [setIsOtherCustomDiseaseSelected]
  );

  const handleSelectOption = useCallback(
    (value: string) => {
      const key = value as OtherDiseaseKey;
      setOtherDiseaseValue(key, !form.other_disease![key]);
    },
    [setOtherDiseaseValue, form]
  );

  const handleOtherDiseaseInputChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      setCustomDiseaseValue(e.target.value);
    },
    [setCustomDiseaseValue]
  );

  const handleSelectFollowUp = useCallback(
    (selected: string) => {
      setOtherDiseaseFollowUp(selected);
    },
    [setOtherDiseaseFollowUp]
  );

  return (
    <>
      <Fieldset>
        <Label htmlFor="other-disease" className="text-center justify-center">
          {t('grade-question-other-disease')}
        </Label>
        <DropdownMenu>
          <div className="flex justify-center">
            <DropdownMenuTrigger disabled={isDisabled} asChild>
              <Button
                id="other-disease"
                variant={otherSelectLabel === t('default-select') ? 'outline-muted' : 'secondary'}
                className="min-w-96 max-w-[640px]"
                disabled={isDisabled}
                size="lg"
              >
                {otherSelectLabel}
              </Button>
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent
            align="start"
            className="max-w-60 md:max-w-96 md:min-w-60 flex flex-col"
          >
            <DropdownMenuCheckboxItem
              checked={isOtherDiseaseNoneSelected}
              onCheckedChange={handleSelectOtherDisease}
            >
              {t('grade-question-other-diseases-options.none')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={isOtherCustomDiseaseSelected}
              onCheckedChange={handleSelectCustomDisease}
            >
              {t('grade-question-other-diseases-options.other')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            {DISEASE_OPTIONS_DROPDOWN.map((option, index) => (
              <Fragment key={option.value}>
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={form.other_disease![option.value]}
                  onCheckedChange={() => handleSelectOption(option.value)}
                  disabled={isDisabled}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
                {index < DISEASE_OPTIONS_DROPDOWN.length - 1 && <DropdownMenuSeparator />}
              </Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Fieldset>

      {/* OTHER OPTION STRING */}
      {isOtherCustomDiseaseSelected && (
        <Textarea
          label={t('grade-question-other-disease-specify')}
          classNameFieldSet="justify-center"
          classNameLabel="justify-center"
          className="md:min-w-96"
          required
          value={form.other_disease?.custom}
          onChange={handleOtherDiseaseInputChange}
          name="other-disease"
          disabled={isDisabled}
        />
      )}

      {enableFollowUpQuestion && (
        <MultiDecisionButtons
          label={t('grade-question-needs-follow-up')}
          disabled={isDisabled}
          options={DISEASE_FOLLOW_UP_OPTIONS}
          selected={form?.other_disease?.follow_up ? [form?.other_disease?.follow_up] : []}
          onSelection={handleSelectFollowUp}
        />
      )}
    </>
  );
}
